import { useEffect, useCallback, useState } from "react";
import { useAuthService } from "../../contexts/AuthContext";
import { RoleService, UserRoleInfo, UserRole } from "../../services/auth";
import { msalInstance } from "../../main";
import { loginRequest } from "../../config/authConfig";
import { useAuthStore } from "../../stores/authStore";

const applyEmptyRoles = () => {
  useAuthStore.getState().setUserProfile(RoleService.createUserRoleInfo([]) as any);
};

export const useUserRoles = () => {
  const userRoleInfo = useAuthStore((state) => state.userProfile) as UserRoleInfo | null;
  const setUserProfile = useAuthStore((state) => state.setUserProfile);

  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const authService = useAuthService();

  const extractUserRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);

    const account = msalInstance.getActiveAccount();
    if (!account) {
      applyEmptyRoles();
      setRolesLoading(false);
      return;
    }

    useAuthStore.getState().loginSuccess(account, { accessToken: '', idToken: '' });

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });

      if (!response?.idToken) {
        throw new Error("No ID token in response");
      }

      const roles = RoleService.extractRolesFromIdToken(response.idToken);
      setUserProfile(RoleService.createUserRoleInfo(roles) as any);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setRolesError(`Error al obtener roles: ${message}`);
      applyEmptyRoles();
    } finally {
      setRolesLoading(false);
    }
  }, [setUserProfile]);

  useEffect(() => {
    extractUserRoles();
    return authService.onAuthStateChanged(extractUserRoles);
  }, [authService, extractUserRoles]);

  const rolesArray = userRoleInfo?.roles ?? [];

  const hasRole = (role: UserRole | string): boolean =>
    rolesArray.includes(role);

  const hasAnyRole = (roles: (UserRole | string)[]): boolean =>
    rolesArray.some((userRole) => roles.includes(userRole));

  const hasAllRoles = (roles: (UserRole | string)[]): boolean =>
    roles.every((role) => rolesArray.includes(role));

  const getUserRoles = (): string[] => userRoleInfo?.roles ?? [];

  const isAdmin = (): boolean => hasRole(UserRole.DEMO_ADMIN);

  const isUser = (): boolean => hasRole(UserRole.DEMO_USER);

  const refreshRoles = () => extractUserRoles();

  return {
    userRoleInfo,
    loading: rolesLoading,
    error: rolesError,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getUserRoles,
    isAdmin,
    isUser,
    refreshRoles,
    roleDescriptions: (userRoleInfo?.roles || []).map((role) => ({
      role,
      description: RoleService.getRoleDescription(role),
    })),
  };
};
